import type { ReactNode } from 'react'

export interface GradientHeroProps {
  eyebrow?: string
  title: ReactNode
  subtitle?: ReactNode
  revisionBadge?: string
  actions?: ReactNode
  meta?: ReactNode
  className?: string
  /** Override the default teal gradient with a custom CSS background. */
  background?: string
}

const linearGradient =
  'linear-gradient(135deg, #0f766e 0%, #0f172a 50%, #065f46 100%)'
const radialOverlay =
  'radial-gradient(circle at 30% 40%, rgba(20,184,166,0.15) 0%, transparent 60%), radial-gradient(circle at 70% 60%, rgba(5,150,105,0.10) 0%, transparent 50%)'

export default function GradientHero({
  eyebrow,
  title,
  subtitle,
  revisionBadge,
  actions,
  meta,
  className,
  background = linearGradient,
}: GradientHeroProps) {
  return (
    <section
      className={
        'relative overflow-hidden text-white text-center px-6 pt-24 pb-20 sm:pt-28 sm:pb-24' +
        (className ? ` ${className}` : '')
      }
      style={{ background }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: radialOverlay }}
      />
      <div className="relative z-[1] max-w-[820px] mx-auto">
        {eyebrow && (
          <p className="text-xs sm:text-sm font-semibold tracking-[0.25em] uppercase text-white/55 mb-6">
            {eyebrow}
          </p>
        )}

        {revisionBadge && (
          <span
            className="inline-block text-[11px] font-bold tracking-[0.15em] uppercase text-teal-100 border border-teal-300/40 rounded-full px-3 py-1 mb-5"
            style={{ background: 'rgba(20,184,166,0.18)' }}
          >
            {revisionBadge}
          </span>
        )}

        <h1 className="font-serif text-[clamp(36px,5vw,52px)] leading-[1.15] text-white mb-4">
          {title}
        </h1>

        {subtitle && (
          <p className="text-lg sm:text-xl text-white/70 leading-[1.55] max-w-[640px] mx-auto mb-8">
            {subtitle}
          </p>
        )}

        {actions && <div className="flex flex-wrap items-center justify-center gap-3">{actions}</div>}

        {meta && <p className="text-sm text-white/50 mt-8">{meta}</p>}
      </div>
    </section>
  )
}
